// src/components/operationsAssociate/BulkAssetUploadModal.js

import React, { useState, useEffect } from "react";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import Select from "react-select";
import axios from "axios";
import { toast } from "react-toastify";

const API_URL = "https://ims.holdthechild.net";

const today = new Date();
const maxDate = today.toISOString().split("T")[0];

const initialAsset = {
    name: "",
    category: "",
    status: "",
    condition: "",
    assignedTo: "",
    custodian: "",
    location: "",
    serialNumber: "",
    purchaseDate: "",
    notes: "",
    quantity: 1,
    unitPrice: 0,
    comprehensivePrice: 0,
};

// Convert to camelCase
const toCamelCase = (str) => {
    return str
        .replace(/[^a-zA-Z0-9]+(.)?/g, (match, chr) =>
            chr ? chr.toUpperCase() : ""
        )
        .replace(/^./, (str) => str.toLowerCase());
};

// Convert DD/MM/YYYY → YYYY-MM-DD
const normalizeDate = (value) => {
    if (!value) return "";
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
        const [day, month, year] = value.split("/");
        return `${year}-${month}-${day}`;
    }
    return value; // already in correct format
};

export default function BulkAssetUploadModal({ closeModal, onAssetsUploaded }) {
    const [assets, setAssets] = useState([initialAsset]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [staffList, setStaffList] = useState([]);

    const staffOptions = staffList.map((staff) => ({
        value: staff._id,
        label: `${staff.firstName} ${staff.lastName}`,
    }));

    useEffect(() => {
        const fetchStaff = async () => {
            const token = localStorage.getItem("token");
            try {
                const res = await axios.get(`${API_URL}/api/staff`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setStaffList(res.data.data || []);
            } catch (err) {
                toast.error("Failed to load staff list");
            }
        };
        fetchStaff();
    }, []);

    const handleChange = (index, e) => {
        const { name, value, type } = e.target;
        const newAssets = [...assets];
        const updatedValue = type === "number" ? parseFloat(value) : value;
        const updatedAsset = { ...newAssets[index], [name]: updatedValue };

        // Real-time comprehensive price
        if (name === "unitPrice" || name === "quantity") {
            const unitPrice =
                parseFloat(name === "unitPrice" ? updatedValue : updatedAsset.unitPrice) ||
                0;
            const quantity =
                parseInt(name === "quantity" ? updatedValue : updatedAsset.quantity) || 0;
            updatedAsset.comprehensivePrice = (unitPrice * quantity).toFixed(2);
        }

        newAssets[index] = updatedAsset;
        setAssets(newAssets);
    };

    const handleSelectChange = (index, name, selected) => {
        const newAssets = [...assets];
        newAssets[index] = {
            ...newAssets[index],
            [name]: selected?.value || "",
        };
        setAssets(newAssets);
    };

    const handleAddRow = () => {
        setAssets([...assets, { ...initialAsset }]);
    };

    const handleRemoveRow = (index) => {
        setAssets(assets.filter((_, i) => i !== index));
    };

    const processImportedData = (data) => {
        if (!data || data.length === 0) {
            setError("The file is empty or has no data.");
            setAssets([initialAsset]);
            return;
        }

        const newAssets = data.map((item) => {
            let assetObject = { ...initialAsset };
            for (const key in item) {
                if (Object.prototype.hasOwnProperty.call(item, key)) {
                    const normalizedKey = toCamelCase(key.trim());
                    if (assetObject.hasOwnProperty(normalizedKey)) {
                        let value = item[key];

                        // Handle numbers
                        if (
                            normalizedKey === "quantity" ||
                            normalizedKey === "unitPrice" ||
                            normalizedKey === "comprehensivePrice"
                        ) {
                            value = parseFloat(value) || 0;
                        }

                        // Handle dates
                        if (normalizedKey === "purchaseDate") {
                            value = normalizeDate(value);
                        }

                        assetObject[normalizedKey] = value;
                    }
                }
            }

            // Recalculate price if needed
            if (
                assetObject.quantity > 0 &&
                assetObject.unitPrice > 0 &&
                assetObject.comprehensivePrice === 0
            ) {
                assetObject.comprehensivePrice = (
                    assetObject.quantity * assetObject.unitPrice
                ).toFixed(2);
            }

            return assetObject;
        });

        const filteredAssets = newAssets.filter((asset) => asset.name);

        if (filteredAssets.length > 0) {
            setAssets(filteredAssets);
            setSuccess(
                "File imported successfully. Review the data below before saving."
            );
        } else {
            setError(
                "The file contains no valid asset data. Please check the 'Name' column."
            );
            setAssets([initialAsset]);
        }
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        setError("");
        setSuccess("");
        if (file) {
            const fileExtension = file.name.split(".").pop().toLowerCase();
            if (fileExtension === "csv") {
                Papa.parse(file, {
                    header: true,
                    skipEmptyLines: true,
                    complete: (results) => {
                        processImportedData(results.data);
                    },
                    error: (err) => {
                        setError("Failed to parse CSV file. Please check the format.");
                        console.error("CSV parse error:", err);
                    },
                });
            } else if (fileExtension === "xlsx") {
                const reader = new FileReader();
                reader.onload = (event) => {
                    try {
                        const data = new Uint8Array(event.target.result);
                        const workbook = XLSX.read(data, { type: "array" });
                        const sheetName = workbook.SheetNames[0];
                        const worksheet = workbook.Sheets[sheetName];
                        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
                            defval: "",
                        });
                        processImportedData(jsonData);
                    } catch (err) {
                        setError(
                            "Failed to parse Excel file. Please ensure it's a valid XLSX file."
                        );
                        console.error("Excel parse error:", err);
                    }
                };
                reader.readAsArrayBuffer(file);
            } else {
                setError("Unsupported file format. Please upload a .csv or .xlsx file.");
            }
        }
    };

    const handleDownloadTemplate = () => {
        const headers = [
            "Name",
            "Category",
            "Status",
            "Condition",
            "Assigned To",
            "Custodian",
            "Location",
            "Serial Number",
            "Purchase Date",
            "Notes",
            "Quantity",
            "Unit Price",
            "Comprehensive Price",
        ];
        const csvContent = headers.join(",");

        const blob = new Blob([csvContent], {
            type: "text/csv;charset=utf-8;",
        });
        const link = document.createElement("a");
        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute("href", url);
            link.setAttribute("download", "asset_template.csv");
            link.style.visibility = "hidden";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    // --- NEW/UPDATED: handleSubmit logic ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");

        // Filter out rows with no asset name
        const assetsToExpand = assets.filter((a) => a.name);

        if (assetsToExpand.length === 0) {
            return setError(
                "No valid assets to add. Please fill in at least the asset name."
            );
        }

        // Create a new array to hold the final, expanded list of assets
        const assetsToSubmit = [];

        assetsToExpand.forEach(asset => {
            const quantity = parseInt(asset.quantity) || 1;
            // Get the comprehensive price from the input field
            const comprehensivePrice = parseFloat(asset.comprehensivePrice) || 0;
            // Calculate the per-unit cost
            const unitPrice = comprehensivePrice > 0 && quantity > 0
                ? (comprehensivePrice / quantity).toFixed(2)
                : (parseFloat(asset.unitPrice) || 0).toFixed(2);

            for (let i = 0; i < quantity; i++) {
                const newAsset = {
                    name: asset.name,
                    category: asset.category,
                    status: asset.status,
                    condition: asset.condition,
                    assignedTo: asset.assignedTo,
                    custodian: asset.custodian,
                    location: asset.location,
                    serialNumber: asset.serialNumber,
                    purchaseDate: asset.purchaseDate,
                    notes: asset.notes,
                    // --- NEW: For a single asset from a bulk upload, its purchase price is its unit price ---
                    purchasePrice: Number(unitPrice),
                    unitPrice: Number(unitPrice),
                };

                // Strip empty staff references
                if (!newAsset.assignedTo) delete newAsset.assignedTo;
                if (!newAsset.custodian) delete newAsset.custodian;

                assetsToSubmit.push(newAsset);
            }
        });

        setLoading(true);
        const token = localStorage.getItem("token");
        if (!token) {
            setLoading(false);
            return setError("You must be logged in.");
        }

        try {
            await axios.post(`${API_URL}/api/assets/bulk`, assetsToSubmit, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });

            toast.success(`${assetsToSubmit.length} asset(s) added successfully! 🎉`);
            onAssetsUploaded();
            closeModal();
        } catch (err) {
            const errorMessage =
                err?.response?.data?.message ||
                err.message ||
                "An unexpected error occurred.";
            toast.error(`Error: ${errorMessage} 😥`);
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };
    // ------------------------------------

    const tableHeaders = [
        "Name",
        "Category",
        "Status",
        "Condition",
        "Assigned To",
        "Custodian",
        "Location",
        "Serial Number",
        "Purchase Date",
        "Notes",
        "Quantity",
        "Unit Price ($)",
        "Comprehensive Price ($)",
    ];

    const renderField = (asset, index, field, type = "text", options = null) => {
        const isRequired = ["name"].includes(field);
        const value = asset[field];

        if (options) {
            return (
                <select
                    name={field}
                    value={value}
                    onChange={(e) => handleChange(index, e)}
                    className="p-2 border rounded w-full text-sm"
                    required={isRequired}
                >
                    <option value="">-- Select --</option>
                    {options.map((opt, i) => (
                        <option key={i} value={opt}>
                            {opt}
                        </option>
                    ))}
                </select>
            );
        }

        if (field === "assignedTo" || field === "custodian") {
            return (
                <Select
                    options={staffOptions}
                    value={staffOptions.find((opt) => opt.value === value)}
                    onChange={(selected) => handleSelectChange(index, field, selected)}
                    className="react-select-container w-full"
                    classNamePrefix="react-select"
                    placeholder="Select..."
                    isClearable
                />
            );
        }

        if (type === "date") {
            return (
                <input
                    type="date"
                    name={field}
                    value={value}
                    onChange={(e) => handleChange(index, e)}
                    className="p-2 border rounded w-full text-sm"
                    required={isRequired}
                    max={maxDate}
                />
            );
        }

        if (type === "number") {
            return (
                <input
                    type="number"
                    name={field}
                    value={value}
                    onChange={(e) => handleChange(index, e)}
                    className="p-2 border rounded w-full text-sm"
                    required={isRequired}
                    min="0"
                    step="0.01"
                />
            );
        }

        if (field === "comprehensivePrice") {
            return (
                <input
                    type="text"
                    name={field}
                    value={parseFloat(value).toFixed(2)}
                    readOnly
                    className="p-2 border rounded w-full text-sm bg-gray-100 cursor-not-allowed"
                />
            );
        }

        return (
            <input
                type={type}
                name={field}
                value={value}
                onChange={(e) => handleChange(index, e)}
                className="p-2 border rounded w-full text-sm"
                required={isRequired}
            />
        );
    };

    const renderTableRow = (asset, index) => (
        <tr key={index} className="border-b hover:bg-gray-50">
            <td className="p-2">{renderField(asset, index, "name")}</td>
            <td className="p-2">
                {renderField(asset, index, "category", "select", [
                    "Electronics",
                    "Furniture",
                    "Vehicles",
                    "Office Supplies",
                    "Other",
                ])}
            </td>
            <td className="p-2">
                {renderField(asset, index, "status", "select", [
                    "Active",
                    "Needs Review",
                    "Under Maintenance",
                    "Damaged",
                    "Disposed",
                ])}
            </td>
            <td className="p-2">
                {renderField(asset, index, "condition", "select", [
                    "New",
                    "Good",
                    "Needs Repair",
                    "Scrap",
                ])}
            </td>
            <td className="p-2">{renderField(asset, index, "assignedTo", "select")}</td>
            <td className="p-2">{renderField(asset, index, "custodian", "select")}</td>
            <td className="p-2">{renderField(asset, index, "location")}</td>
            <td className="p-2">{renderField(asset, index, "serialNumber")}</td>
            <td className="p-2">{renderField(asset, index, "purchaseDate", "date")}</td>
            <td className="p-2">{renderField(asset, index, "notes")}</td>
            <td className="p-2">{renderField(asset, index, "quantity", "number")}</td>
            <td className="p-2">{renderField(asset, index, "unitPrice", "number")}</td>
            <td className="p-2">
                {renderField(asset, index, "comprehensivePrice", "text")}
            </td>
            <td className="p-2">
                <button
                    type="button"
                    onClick={() => handleRemoveRow(index)}
                    className="text-red-500 hover:text-red-700 font-bold p-2"
                >
                    &times;
                </button>
            </td>
        </tr>
    );

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 overflow-y-auto">
            {error && (
                <div className="fixed top-5 left-1/2 transform -translate-x-1/2 z-50 bg-red-500 text-white px-6 py-3 rounded shadow-md animate-fade-in-out">
                    ❌ {error}
                </div>
            )}
            {success && (
                <div className="fixed top-5 left-1/2 transform -translate-x-1/2 z-50 bg-green-500 text-white px-6 py-3 rounded shadow-md animate-fade-in-out">
                    ✅ {success}
                </div>
            )}

            <form
                onSubmit={handleSubmit}
                className="bg-white rounded-xl shadow-xl p-6 w-full max-w-6xl relative space-y-6 max-h-[90vh] overflow-y-auto"
            >
                <button
                    type="button"
                    onClick={closeModal}
                    className="absolute top-2 right-2 text-gray-500 hover:text-red-500 text-xl font-bold"
                >
                    &times;
                </button>
                <h2 className="text-2xl font-semibold text-orange-600">
                    Bulk Add Assets
                </h2>

                <div className="flex flex-col md:flex-row items-center justify-between gap-4 border-b pb-4">
                    <div className="flex-grow">
                        <label className="block text-gray-700 font-semibold mb-1">
                            Upload CSV or Excel File (.xlsx):
                        </label>
                        <input
                            type="file"
                            accept=".csv, .xlsx"
                            onChange={handleFileUpload}
                            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
                        />
                    </div>
                    <div>
                        <span className="text-sm text-gray-500">
                            <a
                                href="#"
                                onClick={handleDownloadTemplate}
                                className="text-orange-500 hover:underline"
                            >
                                Download CSV Template
                            </a>
                        </span>
                    </div>
                </div>

                <div className="overflow-x-auto border rounded">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                {tableHeaders.map((header, i) => (
                                    <th
                                        key={i}
                                        className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                                    >
                                        {header}
                                        {["Name"].includes(header) && (
                                            <span className="text-red-500 ml-1">*</span>
                                        )}
                                    </th>
                                ))}
                                <th className="p-3"></th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {assets.map((asset, index) => renderTableRow(asset, index))}
                        </tbody>
                    </table>
                </div>

                <div className="flex justify-between items-center mt-4">
                    <button
                        type="button"
                        onClick={handleAddRow}
                        className="px-4 py-2 rounded bg-gray-200 text-gray-700 hover:bg-gray-300"
                    >
                        Add Row
                    </button>
                    <div className="flex gap-4">
                        <button
                            type="button"
                            onClick={closeModal}
                            className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-100"
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 rounded bg-orange-600 text-white hover:bg-orange-700"
                            disabled={loading}
                        >
                            {loading ? (
                                <span className="loader border-white"></span>
                            ) : (
                                "Save All Assets"
                            )}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
}